import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Minus, HelpCircle } from 'lucide-react';

/**
 * FAQAccordion — 6-10 questions with rich answers (60-100 words each).
 * Props: { eyebrow?, heading, sub?, items: [{q, a}], isRTL }
 */
const FAQAccordion = ({ eyebrow, heading, sub, items = [], isRTL = false }) => {
  const [open, setOpen] = useState(0);
  return (
    <section className="py-16 lg:py-20" data-testid="faq-accordion">
      <div className="max-w-[920px] mx-auto px-6">
        <div className={`mb-10 ${isRTL ? 'text-right' : ''}`}>
          {eyebrow && <p className="varo-eyebrow varo-text-flame mb-3 inline-flex items-center gap-2"><HelpCircle className="w-4 h-4" /> {eyebrow}</p>}
          <h2 className="varo-display text-3xl lg:text-5xl mb-3 leading-tight">{heading}</h2>
          {sub && <p className="text-ink-600 text-lg">{sub}</p>}
        </div>
        <div className="space-y-3">
          {items.map((it, i) => {
            const isOpen = open === i;
            return (
              <article key={i} className={`bg-white rounded-2xl border ${isOpen ? 'border-flame-300 shadow-sm' : 'border-flame-100'} overflow-hidden transition-all`} data-testid={`faq-${i}`}>
                <button onClick={() => setOpen(isOpen ? -1 : i)} className={`w-full flex items-center justify-between gap-4 p-5 lg:p-6 ${isRTL ? 'flex-row-reverse text-right' : 'text-left'} hover:bg-flame-50/40 transition-colors`} data-testid={`faq-q-${i}`}>
                  <span className={`varo-display text-base lg:text-lg leading-snug ${isOpen ? 'text-flame-700' : 'text-ink-900'}`}>{it.q}</span>
                  <span className={`shrink-0 w-9 h-9 rounded-full flex items-center justify-center transition-all ${isOpen ? 'bg-flame-500 text-white rotate-180' : 'bg-flame-50 text-flame-600'}`}>
                    {isOpen ? <Minus className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                  </span>
                </button>
                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.25 }} className="overflow-hidden">
                      <div className={`px-5 lg:px-6 pb-6 text-ink-700 text-sm lg:text-base leading-[1.8] ${isRTL ? 'text-right' : ''}`} data-testid={`faq-a-${i}`}>
                        {it.a}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default FAQAccordion;
